import { ActivatedRoute } from '@angular/router';
import { AlertService } from '../../shared/services/alert.service';
import { of } from 'rxjs';
import { Component, OnInit } from '@angular/core';
import { forkJoin, switchMap } from 'rxjs';
import { Utils } from '../../core/utils';
import { PatientModel, PatientSessionModel, PatientTargetModel, PatientBehaviorModel, ParentBehaviorOptionsModel } from '../../core/api/models';
import { ParentApiService } from '../../core/api/services';
import { ParentSessionService } from '../collect/parent-session.service';

@Component({
  selector: 'app-parent-home',
  templateUrl: './parent-home.component.html',
  styleUrls: ['./parent-home.component.css'],
  standalone: false,
})
export class ParentHomeComponent implements OnInit {
  offlineMode = false;
  endingSessionIDs = new Set<number>();
  patients: PatientModel[] = [];
  selectedPatient: PatientModel | null = null;
  sessions: PatientSessionModel[] = [];
  targets: PatientTargetModel[] = [];
  behaviors: PatientBehaviorModel[] = [];
  behaviorOptions: ParentBehaviorOptionsModel = {};
  loadingSessions = false;
  private requestVersion = 0;
  private sessionRowsSource: PatientSessionModel[] | null = null;
  private sessionRowsPatientID: number | undefined;
  private cachedSessionRows: PatientSessionModel[] = [];

  selectPatient(patient: PatientModel): void {
    const version = ++this.requestVersion;
    this.selectedPatient = patient;
    this.sessions = [];
    this.loadingSessions = true;
    this.errorMessage = '';
    const cached = this.parentSessionService.getOfflineProfile(patient.ID!);
    const loadSessions = this.offlineMode ? of({
      options: cached?.options ?? {}, sessions: this.parentSessionService.getCachedSessions(patient.ID!),
      targets: cached?.targets ?? [], behaviors: cached?.behaviors ?? []
    }) : this.parentSessionService.syncPendingSessions().pipe(switchMap(() => forkJoin({
      options: this.parentApiService.GetBehaviorOptions({patientID: patient.ID!}),
      sessions: this.parentApiService.GetSessions({patientID: patient.ID!}),
      targets: this.parentApiService.GetTargets({patientID: patient.ID!, onlyActive: false}),
      behaviors: this.parentApiService.GetBehaviors({patientID: patient.ID!, onlyActive: false})
    })));
    loadSessions.subscribe({
      next: data => {
        if (version !== this.requestVersion) return;
        this.parentSessionService.rememberSessions(data.sessions);
        this.sessions = this.parentSessionService.getCachedSessions(patient.ID!);
        this.behaviorOptions = data.options;
        this.targets = data.targets;
        this.behaviors = data.behaviors;
        this.loadingSessions = false;
      },
      error: error => {
        if (version !== this.requestVersion) return;
        if (error.status === 0 && cached) {
          this.sessions = this.parentSessionService.getCachedSessions(patient.ID!);
          this.targets = cached.targets;
          this.behaviors = cached.behaviors;
          this.behaviorOptions = cached.options;
        } else this.errorMessage = Utils.getErrorMessageFromErrorResponse(error);
        this.loadingSessions = false;
      }
    });
  }
  get sessionRows(): PatientSessionModel[] {
    if (!this.selectedPatient) return [];
    // Keep the placeholder row stable while pointer events trigger change detection.
    if (this.sessionRowsSource !== this.sessions || this.sessionRowsPatientID !== this.selectedPatient.ID) {
      this.sessionRowsSource = this.sessions;
      this.sessionRowsPatientID = this.selectedPatient.ID;
      this.cachedSessionRows = this.sessions.some(x => !x.FinishedDate) ? this.sessions : [
        { ID: 0, PatientID: this.selectedPatient.ID, TargetData: [], BehaviorData: [] }, ...this.sessions
      ];
    }
    return this.cachedSessionRows;
  }

  endSession(session: PatientSessionModel): void {
    if (!session.ID || this.endingSessionIDs.has(session.ID)) return;
    if (this.parentSessionService.hasRunningTimers(session.ID)) {
      this.alerts.info('Stop or complete the running timers before ending the session.');
      return;
    }
    const patient = this.selectedPatient!;
    const sessionID = session.ID;
    this.endingSessionIDs.add(sessionID);
    this.parentSessionService.finishSession(session).subscribe(synced => {
      this.endingSessionIDs.delete(sessionID);
      if (!synced) this.alerts.info('Saved on this device. It will be sent when you are back online.');
      if (this.selectedPatient?.ID === patient.ID) this.selectPatient(patient);
    });
  }

  loading: boolean = true;
  errorMessage: string = '';

  constructor(private route: ActivatedRoute, private alerts: AlertService, private parentApiService: ParentApiService, public parentSessionService: ParentSessionService) { }

  ngOnInit(): void {
    this.offlineMode = this.route.snapshot.data['offline'] === true;
    this.parentSessionService.syncPendingSessions().subscribe();
    this.parentSessionService.getPatients(this.offlineMode).subscribe({
      next: (patients) => {
        this.patients = patients ?? [];
        this.loading = false;
        const sessionPatientID = Number(this.route.snapshot.queryParamMap.get('patientID')) || this.parentSessionService.getPatientSession()?.PatientID;
        const patient = this.patients.find(x => x.ID === sessionPatientID) ?? this.patients[0];
        if (patient) this.selectPatient(patient);
      },
      error: (error) => {
        console.error('Error loading parent patients', error);
        this.errorMessage = 'Error loading patients.';
        this.loading = false;
      }
    });
  }
}
